/**
 * RelatedFilesPanel + SideBySideView (Feb 2026 · sprint 374).
 *
 * Related files are supporting docs (action plan, MOM, etc.) attached to a
 * review. `.xlsx` types re-use the existing WorkbookRenderer *read-only*
 * so they can be presented side-by-side with the primary review workbook.
 * No annotation store for related files (per user decision — read-only).
 */
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { toast } from 'sonner';
import { X, Upload, FileSpreadsheet, FileText, Download, Trash2, SplitSquareHorizontal } from 'lucide-react';
import { WorkbookRenderer } from './WorkbookRenderer';

const KIND_LABELS = {
  action_plan: 'Action Plan',
  mom: 'MOM',
  supporting: 'Supporting',
  other: 'Other',
};

export function RelatedFilesPanel({ reviewId, onOpenAlongside, onClose }) {
  const { API, getHeaders } = useAuth();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [kind, setKind] = useState('action_plan');

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/reviews/${reviewId}/related-files`, { headers: getHeaders() });
      if (!r.ok) return;
      setFiles((await r.json()).items || []);
    } catch { /* silent */ }
  }, [API, getHeaders, reviewId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) return toast.error('File too large (>25 MB)');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('kind', kind);
      fd.append('label', file.name);
      fd.append('file', file);
      const r = await fetch(`${API}/reviews/${reviewId}/related-files`, {
        method: 'POST', headers: getHeaders(), body: fd,
      });
      if (!r.ok) {
        const t = await r.text().catch(() => '');
        throw new Error(t.slice(0, 120) || `HTTP ${r.status}`);
      }
      toast.success('File attached');
      load();
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setUploading(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Remove this related file?')) return;
    try {
      const r = await fetch(`${API}/reviews/${reviewId}/related-files/${id}`, {
        method: 'DELETE', headers: getHeaders(),
      });
      if (!r.ok) throw new Error();
      load(); toast.success('Removed');
    } catch { toast.error('Failed'); }
  };

  const download = async (rf) => {
    try {
      const r = await fetch(`${API}/reviews/${reviewId}/related-files/${rf.id}/source`, { headers: getHeaders() });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = rf.filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  return (
    <div className="h-full flex flex-col bg-white border-l border-[#E7EBE6]" data-testid="related-files-panel">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E7EBE6] bg-[#F9FBF7]">
        <h3 className="text-sm font-semibold text-[color:var(--color-text-primary)]">Related Files</h3>
        <button onClick={onClose} data-testid="rf-close" className="p-1 text-[#7A8B82] hover:text-red-600">
          <X className="w-4 h-4"/>
        </button>
      </div>
      <div className="px-3 py-2 border-b border-[#E7EBE6] bg-[#FDFDFC] flex items-center gap-1.5">
        <select value={kind} onChange={(e) => setKind(e.target.value)}
                data-testid="rf-kind"
                className="h-7 text-xs border border-[#E7EBE6] rounded px-1">
          {Object.entries(KIND_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <label className="flex-1">
          <input type="file" hidden data-testid="rf-file-input"
                 onChange={(e) => upload(e.target.files?.[0])}
                 accept=".xlsx,.xlsm,.pdf,.png,.jpg,.jpeg,.doc,.docx,.txt"/>
          <span className={`inline-flex items-center gap-1 h-7 px-2 rounded border border-[#E7EBE6] text-xs cursor-pointer hover:bg-[#F9FBF7] ${uploading ? 'opacity-50' : ''}`}
                data-testid="rf-upload-trigger">
            <Upload className="w-3 h-3"/> {uploading ? 'Uploading…' : 'Attach file'}
          </span>
        </label>
      </div>
      <div className="flex-1 overflow-auto p-2 space-y-1.5">
        {!files.length && <p className="text-xs text-[#B4C0BB] italic text-center py-4" data-testid="rf-empty">No related files.</p>}
        {files.map(rf => {
          const isXlsx = rf.extension === 'xlsx' || rf.extension === 'xlsm';
          const Icon = isXlsx ? FileSpreadsheet : FileText;
          return (
            <div key={rf.id} className="border border-[#EEF1EC] rounded-md p-2 flex items-start gap-2 text-xs"
                 data-testid={`rf-item-${rf.id}`}>
              <Icon className={`w-4 h-4 mt-0.5 ${isXlsx ? 'text-[color:var(--color-primary)]' : 'text-[#7A8B82]'}`}/>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-[#1A2421] truncate" title={rf.filename}>{rf.label || rf.filename}</p>
                <p className="text-[10px] text-[#7A8B82]">
                  {KIND_LABELS[rf.kind] || rf.kind} · {(rf.size_bytes / 1024).toFixed(1)} KB
                </p>
                <div className="flex items-center gap-1 mt-1">
                  {isXlsx && (
                    <Button size="sm" variant="outline" onClick={() => onOpenAlongside?.(rf)}
                            data-testid={`rf-open-alongside-${rf.id}`}
                            className="h-6 text-[10px] gap-1">
                      <SplitSquareHorizontal className="w-3 h-3"/> Open Alongside
                    </Button>
                  )}
                  <button onClick={() => download(rf)} data-testid={`rf-download-${rf.id}`}
                          className="text-[10px] text-[#5C6B64] hover:text-[color:var(--color-primary-hover)] inline-flex items-center gap-0.5">
                    <Download className="w-3 h-3"/> Original
                  </button>
                  <button onClick={() => del(rf.id)} data-testid={`rf-delete-${rf.id}`}
                          className="ml-auto text-[10px] text-red-500 hover:text-red-700 inline-flex items-center gap-0.5">
                    <Trash2 className="w-3 h-3"/>
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── SideBySideRelatedView ─────────────────────────────────────────────
// Renders a related .xlsx workbook read-only alongside the primary one.
// Provides its own sheet tab strip; scrolling / frozen column preserved
// because it uses the same WorkbookRenderer.
export function SideBySideRelatedView({ reviewId, relatedFile, onClose }) {
  const { API, getHeaders } = useAuth();
  const [sheetIndex, setSheetIndex] = useState(0);
  const [sheet, setSheet] = useState(null);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/reviews/${reviewId}/related-files/${relatedFile.id}`, { headers: getHeaders() });
        if (r.ok) setMeta(await r.json());
      } catch { /* silent */ }
    })();
  }, [API, getHeaders, reviewId, relatedFile.id]);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/reviews/${reviewId}/related-files/${relatedFile.id}/sheets/${sheetIndex}`, { headers: getHeaders() });
        if (r.ok) setSheet(await r.json());
      } catch { /* silent */ }
    })();
  }, [API, getHeaders, reviewId, relatedFile.id, sheetIndex]);

  const sheetsMeta = meta?.workbook?.sheets_meta || [];
  const styles = meta?.workbook?.styles || [];

  return (
    <div className="h-full flex flex-col bg-white border-l border-[color:var(--color-primary)]"
         data-testid="side-by-side-related">
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E7EBE6] bg-[color:var(--color-primary-tint)]">
        <div>
          <h3 className="text-sm font-semibold text-[color:var(--color-primary-hover)]">
            {relatedFile.label || relatedFile.filename}
          </h3>
          <p className="text-[10px] text-[#5C6B64]">{KIND_LABELS[relatedFile.kind] || 'Related'} · Read-only</p>
        </div>
        <button onClick={onClose} data-testid="sbs-close" className="p-1 text-[#5C6B64] hover:text-red-600">
          <X className="w-4 h-4"/>
        </button>
      </div>
      {sheetsMeta.length > 1 && (
        <div className="flex gap-1 border-b border-[#E7EBE6] bg-[#F9FBF7] px-2">
          {sheetsMeta.map(s => (
            <button key={s.sheet_index} onClick={() => setSheetIndex(s.sheet_index)}
                    data-testid={`sbs-sheet-tab-${s.sheet_index}`}
                    className={`px-2 py-1.5 text-[11px] font-medium border-b-2 -mb-px ${
                      sheetIndex === s.sheet_index
                        ? 'border-[color:var(--color-primary)] text-[color:var(--color-primary-hover)]'
                        : 'border-transparent text-[#5C6B64]'}`}>
              {s.sheet_name}
            </button>
          ))}
        </div>
      )}
      <div className="flex-1 min-h-0 overflow-hidden">
        {sheet ? (
          <WorkbookRenderer sheet={sheet} styles={styles} annotations={[]}
                             selection={{ ranges: [], cells: [], cols: [], anchor: null, focus: null }}
                             onSelectionChange={() => {}}
                             showRemarks={false} editable={false} presentation={true}/>
        ) : (
          <p className="p-4 text-xs text-[#7A8B82]">Loading…</p>
        )}
      </div>
    </div>
  );
}
